import React, { ReactFragment, useEffect, useState } from 'react';
import { Post as PostModel } from 'redux/models/post';
import { Grid, Paper, Divider, Button, Tooltip, Menu, MenuItem } from '@material-ui/core';
import ThumbUpGrayIcon16 from 'assets/images/thumb-up-gray-16.svg';
import ThumbUpBlueIcon16 from 'assets/images/thumb-up-blue-16.svg';
import Tokens16 from 'assets/images/tokens-16.svg';
import UserIconImage from 'assets/images/user-icon.svg';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { Loader } from 'components/Loader';
import { Subpost as SubpostModel } from 'redux/models/subpost';
import { setupReceivingUsers, setupLikes, addDefaultProfileImgSrc } from 'helpers/reactHelper';
import { Subpost } from 'components/Subpost';
import './Post.scss';
import { formatPostDate } from 'helpers/helper';
import { Team } from 'redux/models/team';
import Chip from '@material-ui/core/Chip';
import { useDispatch } from 'react-redux';
import { addSuccessToast } from 'components/Toast/toastService';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { getProfilePic } from 'api/requests';

interface PostProps {
	post: PostModel;
	userId: string;
	currentUserLikedPost: boolean;
	subpostsLoading: boolean;
	currentTeam: Team | undefined;
	condensed?: boolean;
	deletingPost?: boolean;
	deletingSubpost?: boolean;
	showAdminItems?: boolean;
	addSubpostClicked: (postId: string) => void;
	likePostClicked: (teamId: string, postId: string) => void;
	likeSubpostClicked: (postId: string, subpostId: string) => void;
	showSubpostClicked: (postId: string, showSubposts: boolean) => void;
	deletePostClicked: (post: PostModel) => void;
	handleDeleteSubpostClicked: (subpost: SubpostModel) => void;
}

const Post: React.FC<PostProps> = (props: PostProps) => {
	const {
		post,
		userId,
		currentUserLikedPost,
		subpostsLoading,
		currentTeam,
		condensed,
		deletingPost,
		deletingSubpost,
		showAdminItems,
		addSubpostClicked,
		likePostClicked,
		likeSubpostClicked,
		showSubpostClicked,
		deletePostClicked,
		handleDeleteSubpostClicked,
	} = props;
	const dispatch = useDispatch();
	const [postDate, setPostDate] = useState<string>('');
	const [menuEl, setMenuEl] = React.useState(null);
	const [authorPic, setAuthorPic] = React.useState<string | undefined>();

	const handleAddSubpostClicked = () => {
		addSubpostClicked(post.postId);
	};

	const handleLikePostClicked = () => {
		likePostClicked(post.teamId, post.postId);
	};

	const handleLikeSubpostClicked = (postId: string, subpostId: string) => {
		likeSubpostClicked(postId, subpostId);
	};

	const handleShowSubpostsClicked = () => {
		showSubpostClicked(post.postId, !post.showSubposts as boolean);
	};

	const handleCopyPostLink = () => {
		let protocol = window.location.protocol;
		let hostname = window.location.hostname;
		let port = window.location.port;
		hostname = window.location.hostname === 'localhost' ? `${hostname}:${port}` : hostname;
		let path = `team/${currentTeam?.teamId}/post/${post.postId}`;
		navigator.clipboard.writeText(`${protocol}//${hostname}/${path}`);
		addSuccessToast(dispatch, 'Link to post copied to clipboard!');
	};

	const hasCurrentUserLikedSubpost = function (subpost: SubpostModel) {
		return subpost.likes?.some(like => like.id === userId);
	};

	const setupSubposts = function (subposts: SubpostModel[]) {
		let subpostsList: ReactFragment[] = [];
		for (let i = 0; i < subposts.length; i++) {
			const subpost = subposts[i];
			subpostsList.push(
				<React.Fragment key={i}>
					{i > 0 && <Divider className='Divider' />}
					<Subpost
						subpost={subpost}
						showAdminItems={showAdminItems}
						deletingSubpost={deletingSubpost}
						currentUserLikedSubpost={hasCurrentUserLikedSubpost(subpost)}
						likeSubpostClicked={handleLikeSubpostClicked}
						deleteSubpostClicked={handleDeleteSubpostClicked}
					></Subpost>
				</React.Fragment>
			);
		}

		return subpostsList;
	};

	const removePostClicked = () => {
		handleCloseMenu();
		deletePostClicked(post);
	};

	const handleClickMenu = (event: any) => {
		setMenuEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setMenuEl(null);
	};

	const getProfilePicForAuthor = async (authorEmail: string) => {
		const pic = await getProfilePic(authorEmail);
		setAuthorPic(pic);
	};

	useEffect(() => {
		let formattedDate = formatPostDate(post.createdOn);
		if (!formattedDate.includes('Today') && !formattedDate.includes('Yesterday')) {
			formattedDate = `on ${formattedDate}`;
		}
		setPostDate(formattedDate);
		getProfilePicForAuthor(post.authorEmail);
	}, [post.createdOn, post.authorEmail]);

	return (
		<>
			<Grid item className={post.refType === 'RedactedPost' ? 'post-item--redacted' : 'post-item'}>
				<Paper elevation={2} className={`post-item-paper ${post.refType === 'shoutout' ? 'glow2' : ''}`}>
					{post.refType !== 'RedactedPost' && (
						<>
							<MoreHorizIcon className='post-more' fontSize='small' aria-controls='simple-menu' aria-haspopup='true' onClick={handleClickMenu} />

							<Menu
								id='simple-menu'
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								getContentAnchorEl={null}
								anchorEl={menuEl}
								keepMounted
								open={Boolean(menuEl)}
								onClose={handleCloseMenu}
							>
								<MenuItem dense onClick={handleCopyPostLink}>
									Copy Link
								</MenuItem>
								{showAdminItems && (
									<MenuItem dense disabled={post.refType === 'RedactedPost' || deletingPost} onClick={removePostClicked}>
										Delete Post
									</MenuItem>
								)}
							</Menu>
						</>
					)}

					<Grid container direction='row' className='post-item__title'>
						<Grid item className='post-item__title-image'>
							{post.authorEmail?.length && authorPic ? (
								<img className='post-item__title-image-profile' src={authorPic} onError={e => addDefaultProfileImgSrc(e)} alt='' />
							) : (
								<img src={UserIconImage} alt='user-icon' />
							)}
						</Grid>
						<Grid item className='post-item__title-content'>
							<Grid item container alignItems='center'>
								<div className='post-item__title-content-tokens'>{post.refType !== 'shoutout' ? `+${post.tokens} TOKENS` : 'SHOUT-OUT'}</div>
								{post.refType && post.refType === 'subpost' && <Chip size='small' label='subpost' className='post-item__title-content--chip' />}
							</Grid>

							<div className='post-item__title-content-author'>
								from {post.authorName} {postDate}
							</div>
						</Grid>
					</Grid>
					<Grid className='post-item__comment' {...(condensed && { style: { paddingBottom: '2rem' } })}>
						{post.refType !== 'RedactedPost' && post.users && setupReceivingUsers(post.users)}
						<span className='post-item__comment-content'>{post.comment}</span>
					</Grid>
					{!condensed && (
						<>
							{post.subpostCount > 0 && (
								<Grid className='post-item__subpost-btn'>
									<Button
										className='SubpostButton'
										onClick={handleShowSubpostsClicked}
										startIcon={!post.showSubposts ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
									>
										{post.subpostCount} contributions
									</Button>
								</Grid>
							)}
							<Grid container direction='row' justifyContent='flex-end' className='post-item__footer'>
								<Tooltip title={setupLikes(post.likes)} placement='top'>
									<span
										className={`post-item__footer-like ${
											currentTeam?.adminOnlyPosts && !currentTeam?.isAdmin && post.refType === 'shoutout' ? 'like-btn--padding-right' : ''
										}`}
									>
										<Button
											disabled={post.refType === 'RedactedPost'}
											className={`LikeButton ${currentUserLikedPost ? 'LikeButton-blue' : ''}`}
											onClick={() => handleLikePostClicked()}
										>
											<img src={`${currentUserLikedPost ? ThumbUpBlueIcon16 : ThumbUpGrayIcon16}`} alt='thumb-up-icon' />
											<span className='LikeButton-count'>{post.likes.length}</span>
										</Button>
									</span>
								</Tooltip>
								{!currentTeam?.isLocked && (!currentTeam?.adminOnlyPosts || currentTeam?.isAdmin) && (
									<Button
										disabled={post.refType === 'RedactedPost'}
										title='Send more tokens for this post'
										className={`AddButton ${post.tokenTotal < 1 ? 'right-padding' : ''} `}
										onClick={() => handleAddSubpostClicked()}
									>
										+ Add
									</Button>
								)}
								{post.tokenTotal > 0 && (
									<div className='post-item__footer-total'>
										<div>
											<img src={Tokens16} alt='token-icon' />
										</div>
										<span className='post-item__footer-total-content'>{post.tokenTotal} Total</span>
									</div>
								)}
							</Grid>
							{post.refType === 'shoutout' && (
								<div className='shoutout-donate-container'>
									<div className='shoutout-donate-msg'>{`Consider sending tokens to the ${
										post.users.length > 1 ? 'recipients' : 'recipient'
									}!`}</div>
								</div>
							)}
							{post.showSubposts && (
								<>
									<Divider className='SubpostsDivider' />
									<Grid className='post-item__subposts'>
										{subpostsLoading && <Loader />}
										{!subpostsLoading && post.subposts?.length > 0 && setupSubposts(post.subposts)}
									</Grid>
								</>
							)}
						</>
					)}
				</Paper>
			</Grid>
		</>
	);
};

Post.defaultProps = {
	condensed: true,
	deletingPost: false,
	deletingSubpost: false,
};

export default Post;
